import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/client';
import './Reviews.css';

/* ── Constants ── */
const SEASON_LABELS = { spring: 'Spring', summer: 'Summer', autumn: 'Autumn', winter: 'Winter' };
const SEASON_ICONS  = { spring: '🌸', summer: '☀️', autumn: '🍂', winter: '❄️' };
const TRAVEL_LABELS = { solo: 'Solo', couple: 'Couple', family: 'Family', friends: 'Friends', business: 'Business' };
const HIGHLIGHT_OPTIONS = [
  { key: 'views',     label: 'Mountain Views' },
  { key: 'pool',      label: 'Heated Pool' },
  { key: 'fireplace', label: 'Fireplace' },
  { key: 'food',      label: 'Local Cuisine' },
  { key: 'staff',     label: 'Hospitality' },
  { key: 'trails',    label: 'Forest Trails' },
  { key: 'silence',   label: 'Peace & Quiet' },
  { key: 'stargazing',label: 'Stargazing' },
];

const QUOTES = [
  { text: 'The mountains are calling and I must go.', author: 'John Muir' },
  { text: 'In every walk with nature one receives far more than he seeks.', author: 'John Muir' },
  { text: 'The clearest way into the Universe is through a forest wilderness.', author: 'John Muir' },
];

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? 'Just now' : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return days === 1 ? 'Yesterday' : `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StarRating({ rating, interactive, onChange, size = 'md' }) {
  const [hover, setHover] = useState(0);
  return (
    <div className={`rv__stars rv__stars--${size}${interactive ? ' rv__stars--interactive' : ''}`}>
      {[1,2,3,4,5].map(i => (
        <button
          key={i}
          type="button"
          className={`rv__star${i <= (hover || rating) ? ' rv__star--filled' : ''}${interactive && i <= hover ? ' rv__star--hover' : ''}`}
          onClick={() => interactive && onChange?.(i)}
          onMouseEnter={() => interactive && setHover(i)}
          onMouseLeave={() => interactive && setHover(0)}
          disabled={!interactive}
          aria-label={`${i} star${i > 1 ? 's' : ''}`}
        >
          <svg viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════ */
export default function ReviewsPage() {
  const { user } = useAuth();
  const quoteRef = useRef(QUOTES[Math.floor(Math.random() * QUOTES.length)]);

  /* Data */
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [myVotes, setMyVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState({});

  /* Form */
  const [bookOpen, setBookOpen] = useState(false);
  const [formData, setFormData] = useState({
    rating: 0, title: '', body: '', visit_season: '', travel_type: '', highlights: [], would_return: true,
  });
  const [formStep, setFormStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [successAnim, setSuccessAnim] = useState(false);

  /* Filter */
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  /* ── Fetch reviews ── */
  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getReviews();
      setReviews(data.reviews || []);
      setStats(data.stats || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  useEffect(() => {
    if (user) {
      api.getMyVotes().then(d => setMyVotes(d.votes || [])).catch(() => {});
    }
  }, [user]);

  /* ── Scroll lock when modal is open ── */
  useEffect(() => {
    if (bookOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const top = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (top) window.scrollTo(0, -parseInt(top || '0'));
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [bookOpen]);

  /* ── Filtered & sorted reviews ── */
  const displayed = useMemo(() => {
    let list = [...reviews];
    if (filter !== 'all') list = list.filter(r => r.rating === Number(filter));
    if (sortBy === 'newest') list.sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sortBy === 'oldest') list.sort((a,b) => new Date(a.created_at) - new Date(b.created_at));
    else if (sortBy === 'highest') list.sort((a,b) => b.rating - a.rating);
    else if (sortBy === 'lowest') list.sort((a,b) => a.rating - b.rating);
    else if (sortBy === 'helpful') list.sort((a,b) => b.helpful_count - a.helpful_count);
    return list;
  }, [reviews, filter, sortBy]);

  /* ── Form helpers ── */
  const updateForm = (key, val) => setFormData(p => ({ ...p, [key]: val }));
  const toggleHighlight = (key) => setFormData(p => ({
    ...p,
    highlights: p.highlights.includes(key) ? p.highlights.filter(h => h !== key) : [...p.highlights, key],
  }));

  const canProceed = () => {
    if (formStep === 0) return formData.rating > 0;
    if (formStep === 1) return formData.title.trim().length >= 3 && formData.body.trim().length >= 10;
    return true;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      await api.createReview(formData);
      setSuccessAnim(true);
      setTimeout(() => {
        setSuccessAnim(false);
        setFormData({ rating: 0, title: '', body: '', visit_season: '', travel_type: '', highlights: [], would_return: true });
        setFormStep(0);
        setBookOpen(false);
        fetchReviews();
      }, 2200);
    } catch (e) {
      setSubmitMsg({ type: 'error', text: e.message || 'Something went wrong.' });
    } finally { setSubmitting(false); }
  };

  const handleHelpful = async (reviewId) => {
    if (!user) return;
    try {
      const res = await api.toggleHelpful(reviewId);
      if (res.voted) setMyVotes(p => [...p, reviewId]);
      else setMyVotes(p => p.filter(id => id !== reviewId));
      setReviews(prev => prev.map(r => r.id === reviewId
        ? { ...r, helpful_count: r.helpful_count + (res.voted ? 1 : -1) }
        : r
      ));
    } catch (e) { console.error(e); }
  };

  const toggleExpand = (id) => setExpandedCards(p => ({ ...p, [id]: !p[id] }));

  /* ── Rating bar width ── */
  const ratingBar = (count) => {
    if (!stats || !stats.total) return 0;
    return (count / stats.total) * 100;
  };

  const TOTAL_STEPS = 3;
  const myReviewCount = user ? reviews.filter(r => r.user_id === user.id).length : 0;

  return (
    <div className="rv page-enter">

      {/* ═══ HERO ═══ */}
      <section className="rv__hero">
        {/* Abstract background */}
        <div className="rv__hero-bg" aria-hidden="true">
          <div className="rv__hero-glow rv__hero-glow--1" />
          <div className="rv__hero-glow rv__hero-glow--2" />
          <div className="rv__hero-glow rv__hero-glow--3" />
          <div className="rv__hero-grid" />
          <div className="rv__hero-veil" />
        </div>

        {/* Floating decorative orbs */}
        <div className="rv__hero-orbs" aria-hidden="true">
          <div className="rv__hero-orb rv__hero-orb--1" />
          <div className="rv__hero-orb rv__hero-orb--2" />
          <div className="rv__hero-orb rv__hero-orb--3" />
        </div>

        {/* Animated corner frames */}
        <div className="rv__hero-frame rv__hero-frame--tl" aria-hidden="true" />
        <div className="rv__hero-frame rv__hero-frame--tr" aria-hidden="true" />
        <div className="rv__hero-frame rv__hero-frame--bl" aria-hidden="true" />
        <div className="rv__hero-frame rv__hero-frame--br" aria-hidden="true" />

        {/* Floating gold particles */}
        <div className="rv__hero-particles" aria-hidden="true">
          {[...Array(8)].map((_, i) => (
            <span key={i} className={`rv__hero-particle rv__hero-particle--${(i % 3) + 1}`}
              style={{
                left: `${10 + i * 11}%`,
                animationDelay: `${i * 0.8}s`,
                animationDuration: `${6 + (i % 3) * 2}s`,
              }}
            />
          ))}
        </div>

        {/* Horizontal scan line */}
        <div className="rv__hero-scanline" aria-hidden="true" />

        {/* Centered content */}
        <div className="rv__hero-content rv__anim rv__anim--1">
          <div className="rv__hero-eyebrow">
            <span className="rv__hero-eyebrow-rule" />
            <span className="rv__hero-eyebrow-text">Stories from Our Guests</span>
            <span className="rv__hero-eyebrow-rule" />
          </div>
          <h1 className="rv__hero-title">The Guest <em>Book</em></h1>
          <p className="rv__hero-sub">Every stay writes a new chapter in our story</p>
        </div>
      </section>

      {/* ═══ STATS RIBBON ═══ */}
      {stats && stats.total > 0 && (
        <section className="rv__stats rv__anim rv__anim--2">
          <div className="rv__stats-bg-orb rv__stats-bg-orb--1" />
          <div className="rv__stats-bg-orb rv__stats-bg-orb--2" />
          <div className="rv__stats-inner">
            <div className="rv__stats-score">
              <div className="rv__stats-score-ring">
                <span className="rv__stats-number">{Number(stats.average).toFixed(1)}</span>
              </div>
              <StarRating rating={Math.round(Number(stats.average))} size="sm" />
              <span className="rv__stats-count">
                Based on <strong>{stats.total}</strong> review{stats.total !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="rv__stats-divider" />

            <div className="rv__stats-bars">
              {[5,4,3,2,1].map(n => {
                const count = stats[['','one','two','three','four','five'][n]];
                return (
                  <div key={n} className="rv__stats-bar-row">
                    <span className="rv__stats-bar-label">{n}</span>
                    <svg className="rv__stats-bar-star" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                    <div className="rv__stats-bar-track">
                      <div className="rv__stats-bar-fill" style={{ width: `${ratingBar(count)}%` }} />
                    </div>
                    <span className="rv__stats-bar-count">{count}</span>
                  </div>
                );
              })}
            </div>

            <div className="rv__stats-divider" />

            <div className="rv__stats-highlights">
              <div className="rv__stats-highlight">
                <div className="rv__stats-highlight-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/></svg>
                </div>
                <span className="rv__stats-highlight-value">{stats.would_return_count || 0}</span>
                <span className="rv__stats-highlight-label">Would return</span>
              </div>
              <div className="rv__stats-highlight">
                <div className="rv__stats-highlight-icon rv__stats-highlight-icon--gold">
                  <svg viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                </div>
                <span className="rv__stats-highlight-value">{stats.five || 0}</span>
                <span className="rv__stats-highlight-label">Perfect stays</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══ FEATURED REVIEW ═══ */}
      {reviews.length > 0 && (() => {
        const featured = reviews.reduce((best, r) => (!best || r.rating > best.rating || (r.rating === best.rating && r.helpful_count > best.helpful_count)) ? r : best, null);
        if (!featured || featured.rating < 4) return null;
        return (
          <section className="rv__featured rv__anim rv__anim--3">
            <div className="rv__featured-inner">
              <div className="rv__featured-bg-leaf rv__featured-bg-leaf--1" />
              <div className="rv__featured-bg-leaf rv__featured-bg-leaf--2" />
              <div className="rv__featured-label">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                Featured Story
              </div>
              <div className="rv__featured-quote-mark">"</div>
              <blockquote className="rv__featured-body">{featured.body}</blockquote>
              <div className="rv__featured-footer">
                <div className="rv__featured-avatar">
                  <span>{featured.full_name?.[0]?.toUpperCase() || '?'}</span>
                </div>
                <div className="rv__featured-author">
                  <span className="rv__featured-name">{featured.full_name}</span>
                  <StarRating rating={featured.rating} size="sm" />
                </div>
                {featured.visit_season && (
                  <span className="rv__featured-season">
                    {SEASON_ICONS[featured.visit_season]} {SEASON_LABELS[featured.visit_season]}
                  </span>
                )}
              </div>
            </div>
          </section>
        );
      })()}

      {/* ═══ QUOTE DIVIDER ═══ */}
      <section className="rv__quote-section rv__anim rv__anim--4">
        <div className="rv__quote-inner">
          <div className="rv__quote-mark rv__quote-mark--open">"</div>
          <blockquote className="rv__quote-text">{quoteRef.current.text}</blockquote>
          <cite className="rv__quote-author">— {quoteRef.current.author}</cite>
          <div className="rv__quote-mark rv__quote-mark--close">"</div>
        </div>
        <div className="rv__quote-ornament">
          <span /><span /><span />
        </div>
      </section>

      {/* ═══ WRITE CTA ═══ */}
      <section className="rv__write-cta rv__anim rv__anim--5">
        <div className="rv__write-cta-inner">
          {/* Animated ink blots */}
          <div className="rv__write-cta-blot rv__write-cta-blot--1" />
          <div className="rv__write-cta-blot rv__write-cta-blot--2" />
          {/* Floating decorative quills */}
          <div className="rv__write-cta-float rv__write-cta-float--1" />
          <div className="rv__write-cta-float rv__write-cta-float--2" />
          <div className="rv__write-cta-float rv__write-cta-float--3" />

          <div className="rv__write-cta-book-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
              <line x1="8" y1="7" x2="16" y2="7"/><line x1="8" y1="11" x2="14" y2="11"/>
            </svg>
            <div className="rv__write-cta-book-glow" />
          </div>

          <div className="rv__write-cta-text">
            <h3>Share Your <em>Mountain Story</em></h3>
            <p>Open the guest book and let future travellers discover Cabana Piatra Buru through your eyes</p>
            {myReviewCount > 0 && (
              <span className="rv__write-cta-badge">
                You've shared {myReviewCount} {myReviewCount === 1 ? 'story' : 'stories'} so far
              </span>
            )}
          </div>

          {user ? (
            <button className="rv__write-cta-btn" onClick={() => setBookOpen(true)}>
              <span className="rv__write-cta-btn-text">Write a Story</span>
              <span className="rv__write-cta-btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.24 12.24a6 6 0 00-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/>
                </svg>
              </span>
            </button>
          ) : (
            <Link to="/login" className="rv__write-cta-btn rv__write-cta-btn--login">
              <span className="rv__write-cta-btn-text">Sign In to Write</span>
              <span className="rv__write-cta-btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </span>
            </Link>
          )}
        </div>
      </section>

      {/* ═══ GUEST BOOK MODAL ═══ */}
      {bookOpen && ReactDOM.createPortal(
        <div className="rv__book-overlay" onClick={(e) => { if (e.target === e.currentTarget) setBookOpen(false); }}>
          <div className={`rv__book${successAnim ? ' rv__book--success' : ''}`}>
            {/* Book binding decoration */}
            <div className="rv__book-spine" />

            {/* Page line decorations */}
            <div className="rv__book-lines" />

            {/* Close */}
            <button className="rv__book-close" onClick={() => setBookOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>

            {/* Success overlay */}
            {successAnim && (
              <div className="rv__book-success">
                <div className="rv__book-success-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <h3>Story Written!</h3>
                <p>Your words are now part of our mountain legacy</p>
                <div className="rv__book-success-particles">
                  {[...Array(16)].map((_,i) => <span key={i} style={{ '--i': i }} />)}
                </div>
              </div>
            )}

            {/* Header */}
            {!successAnim && (
              <>
                <div className="rv__book-header">
                  <div className="rv__book-header-ornament">
                    <span /><span /><span />
                  </div>
                  <h2 className="rv__book-title">Write in the Guest Book</h2>
                  <p className="rv__book-subtitle">Your words become part of our mountain story</p>

                  {/* Step progress bar */}
                  <div className="rv__book-progress">
                    <div className="rv__book-progress-track">
                      <div className="rv__book-progress-fill" style={{ width: `${((formStep + 1) / TOTAL_STEPS) * 100}%` }} />
                    </div>
                    <div className="rv__book-steps">
                      {['Rating', 'Story', 'Details'].map((label, i) => (
                        <div key={i} className={`rv__book-step${formStep === i ? ' rv__book-step--active' : ''}${formStep > i ? ' rv__book-step--done' : ''}`}>
                          <span className="rv__book-step-num">
                            {formStep > i ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                            ) : i + 1}
                          </span>
                          <span className="rv__book-step-label">{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Pages */}
                <div className="rv__book-pages">
                  {/* Step 0: Rating */}
                  {formStep === 0 && (
                    <div className="rv__book-page rv__book-page--enter" key="step-0">
                      <div className="rv__book-page-icon">
                        <svg viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      </div>
                      <p className="rv__book-page-prompt">How was your stay at Cabana Piatra Buru?</p>
                      <StarRating rating={formData.rating} interactive onChange={(v) => updateForm('rating', v)} size="lg" />
                      <div className={`rv__book-page-hint${formData.rating ? ' rv__book-page-hint--visible' : ''}`}>
                        {formData.rating === 0 && 'Tap a star to begin'}
                        {formData.rating === 1 && "We're sorry \u2014 tell us what went wrong"}
                        {formData.rating === 2 && "We'd love to hear how we can improve"}
                        {formData.rating === 3 && 'A solid stay \u2014 what could be better?'}
                        {formData.rating === 4 && 'Wonderful! What made it great?'}
                        {formData.rating === 5 && 'A perfect mountain retreat!'}
                      </div>
                    </div>
                  )}

                  {/* Step 1: Title + Body */}
                  {formStep === 1 && (
                    <div className="rv__book-page rv__book-page--enter" key="step-1">
                      <div className="rv__book-field">
                        <label className="rv__book-label">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          Title your story
                        </label>
                        <input
                          className="rv__book-input"
                          type="text"
                          placeholder="e.g. A magical weekend in the mountains..."
                          value={formData.title}
                          onChange={e => updateForm('title', e.target.value)}
                          maxLength={120}
                          autoFocus
                        />
                        <div className="rv__book-input-accent" />
                        <span className="rv__book-char-count">{formData.title.length}/120</span>
                      </div>
                      <div className="rv__book-field">
                        <label className="rv__book-label">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                          Tell your story
                        </label>
                        <textarea
                          className="rv__book-textarea"
                          placeholder="What made your stay special? The morning mist? The fireplace warmth? The star-filled sky?"
                          value={formData.body}
                          onChange={e => updateForm('body', e.target.value)}
                          rows={5}
                        />
                        <div className="rv__book-input-accent" />
                      </div>
                    </div>
                  )}

                  {/* Step 2: Details */}
                  {formStep === 2 && (
                    <div className="rv__book-page rv__book-page--enter" key="step-2">
                      <div className="rv__book-field">
                        <label className="rv__book-label">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          When did you visit?
                        </label>
                        <div className="rv__book-seasons">
                          {Object.entries(SEASON_LABELS).map(([key, label]) => (
                            <button key={key} type="button"
                              className={`rv__book-season${formData.visit_season === key ? ' rv__book-season--active' : ''}`}
                              onClick={() => updateForm('visit_season', key)}
                            >
                              <span className="rv__book-season-icon">{SEASON_ICONS[key]}</span>
                              <span>{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="rv__book-field">
                        <label className="rv__book-label">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                          Who did you travel with?
                        </label>
                        <div className="rv__book-travel-types">
                          {Object.entries(TRAVEL_LABELS).map(([key, label]) => (
                            <button key={key} type="button"
                              className={`rv__book-travel${formData.travel_type === key ? ' rv__book-travel--active' : ''}`}
                              onClick={() => updateForm('travel_type', key)}
                            >{label}</button>
                          ))}
                        </div>
                      </div>

                      <div className="rv__book-field">
                        <label className="rv__book-label">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                          What stood out?
                        </label>
                        <div className="rv__book-highlights">
                          {HIGHLIGHT_OPTIONS.map(({ key, label }) => (
                            <button key={key} type="button"
                              className={`rv__book-highlight${formData.highlights.includes(key) ? ' rv__book-highlight--active' : ''}`}
                              onClick={() => toggleHighlight(key)}
                            >{label}</button>
                          ))}
                        </div>
                      </div>

                      <div className="rv__book-field rv__book-field--return">
                        <label className="rv__book-label">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
                          Would you return?
                        </label>
                        <button type="button"
                          className={`rv__book-return${formData.would_return ? ' rv__book-return--yes' : ''}`}
                          onClick={() => updateForm('would_return', !formData.would_return)}
                        >
                          <span className="rv__book-return-thumb">
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/></svg>
                          </span>
                          <span>{formData.would_return ? 'Absolutely!' : 'Not sure'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="rv__book-nav">
                  {formStep > 0 && (
                    <button className="rv__book-nav-btn rv__book-nav-btn--back" onClick={() => setFormStep(s => s - 1)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="19 12 5 12"/><polyline points="12 19 5 12 12 5"/></svg>
                      Back
                    </button>
                  )}
                  <div className="rv__book-nav-spacer" />
                  {formStep < TOTAL_STEPS - 1 ? (
                    <button className="rv__book-nav-btn rv__book-nav-btn--next" disabled={!canProceed()} onClick={() => setFormStep(s => s + 1)}>
                      Continue
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="5 12 19 12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </button>
                  ) : (
                    <button className="rv__book-nav-btn rv__book-nav-btn--submit" disabled={submitting || !canProceed()} onClick={handleSubmit}>
                      {submitting ? (
                        <><div className="rv__book-nav-spinner" /> Writing...</>
                      ) : (
                        <>Sign the Book <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.24 12.24a6 6 0 00-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/></svg></>
                      )}
                    </button>
                  )}
                </div>

                {submitMsg && (
                  <div className={`rv__book-msg rv__book-msg--${submitMsg.type}`}>
                    {submitMsg.text}
                  </div>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ═══ REVIEWS LIST ═══ */}
      <section className="rv__list-section">
        {/* Floating background elements */}
        <div className="rv__list-bg-elements">
          <div className="rv__list-bg-circle rv__list-bg-circle--1" />
          <div className="rv__list-bg-circle rv__list-bg-circle--2" />
          <div className="rv__list-bg-circle rv__list-bg-circle--3" />
          <div className="rv__list-bg-leaf rv__list-bg-leaf--1" />
          <div className="rv__list-bg-leaf rv__list-bg-leaf--2" />
        </div>

        <div className="rv__list-inner">

          {/* Section header */}
          <div className="rv__list-header rv__anim rv__anim--1">
            <h2 className="rv__list-title">
              Guest <em>Stories</em>
              {stats && <span className="rv__list-count">{stats.total}</span>}
            </h2>
            <div className="rv__list-title-line" />
          </div>

          {/* Filters */}
          <div className="rv__filters rv__anim rv__anim--2">
            <div className="rv__filters-left">
              <button className={`rv__filter-btn${filter === 'all' ? ' rv__filter-btn--active' : ''}`} onClick={() => setFilter('all')}>
                All Reviews
              </button>
              {[5,4,3,2,1].map(n => (
                <button key={n} className={`rv__filter-btn${filter === String(n) ? ' rv__filter-btn--active' : ''}`} onClick={() => setFilter(String(n))}>
                  {n} <svg viewBox="0 0 24 24" className="rv__filter-star-icon"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                </button>
              ))}
            </div>
            <div className="rv__filters-right">
              <select className="rv__sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="highest">Highest Rated</option>
                <option value="lowest">Lowest Rated</option>
                <option value="helpful">Most Helpful</option>
              </select>
            </div>
          </div>

          {/* Review cards */}
          {loading ? (
            <div className="rv__loading">
              <div className="rv__loading-book">
                <div className="rv__loading-page rv__loading-page--1" />
                <div className="rv__loading-page rv__loading-page--2" />
                <div className="rv__loading-page rv__loading-page--3" />
              </div>
              <p>Opening the guest book...</p>
            </div>
          ) : displayed.length === 0 ? (
            <div className="rv__empty rv__anim rv__anim--3">
              <div className="rv__empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
                </svg>
              </div>
              <h3>The guest book awaits</h3>
              <p>{filter !== 'all' ? 'No reviews match this filter' : 'Be the first to share your mountain experience'}</p>
            </div>
          ) : (
            <div className="rv__cards">
              {displayed.map((review, idx) => {
                const isLong = review.body && review.body.length > 280;
                const isExpanded = expandedCards[review.id];
                return (
                  <article
                    key={review.id}
                    className={`rv__card rv__card-anim${review.user_id === user?.id ? ' rv__card--mine' : ''}`}
                    style={{ animationDelay: `${Math.min(idx * 0.08, 0.5)}s` }}
                  >
                    {/* Decorative corners */}
                    <div className="rv__card-corner rv__card-corner--tl" />
                    <div className="rv__card-corner rv__card-corner--tr" />
                    <div className="rv__card-corner rv__card-corner--bl" />
                    <div className="rv__card-corner rv__card-corner--br" />

                    {/* Background shimmer */}
                    <div className="rv__card-shimmer" />

                    {/* Page fold */}
                    <div className="rv__card-fold" />

                    {/* Rating glow */}
                    <div className="rv__card-glow" style={{ '--glow-hue': review.rating >= 4 ? '42' : review.rating >= 3 ? '30' : '0' }} />

                    {/* Header */}
                    <div className="rv__card-header">
                      <div className="rv__card-avatar">
                        <span>{review.full_name?.[0]?.toUpperCase() || '?'}</span>
                        <div className="rv__card-avatar-ring" />
                        {review.user_bookings >= 3 && <div className="rv__card-avatar-badge" title="Loyal guest">&#9733;</div>}
                      </div>
                      <div className="rv__card-author">
                        <span className="rv__card-name">{review.full_name}</span>
                        <span className="rv__card-meta">
                          {review.travel_type && <span className="rv__card-meta-tag">{TRAVEL_LABELS[review.travel_type]}</span>}
                          {review.visit_season && (
                            <span className="rv__card-season">
                              {SEASON_ICONS[review.visit_season]} {SEASON_LABELS[review.visit_season]}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="rv__card-rating-badge" data-rating={review.rating}>
                        <span>{review.rating}.0</span>
                        <svg viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      </div>
                    </div>

                    {/* Stars + date row */}
                    <div className="rv__card-stars-row">
                      <StarRating rating={review.rating} size="sm" />
                      <span className="rv__card-time" title={formatDate(review.created_at)}>{timeAgo(review.created_at)}</span>
                    </div>

                    {/* Content */}
                    <h3 className="rv__card-title">{review.title}</h3>
                    <div className={`rv__card-body-wrap${isLong && !isExpanded ? ' rv__card-body-wrap--truncated' : ''}`}>
                      <p className="rv__card-body">{review.body}</p>
                      {isLong && !isExpanded && <div className="rv__card-body-fade" />}
                    </div>
                    {isLong && (
                      <button className="rv__card-read-more" onClick={() => toggleExpand(review.id)}>
                        {isExpanded ? 'Show less' : 'Read full story'}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }}>
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                    )}

                    {/* Highlights */}
                    {review.highlights && review.highlights.length > 0 && (
                      <div className="rv__card-highlights">
                        {review.highlights.map(h => (
                          <span key={h} className="rv__card-highlight-tag">
                            {HIGHLIGHT_OPTIONS.find(o => o.key === h)?.label || h}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="rv__card-footer">
                      {review.would_return && (
                        <span className="rv__card-return">
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/></svg>
                          Would return
                        </span>
                      )}
                      <div className="rv__card-footer-spacer" />
                      {user && review.user_id !== user.id && (
                        <button
                          className={`rv__card-helpful${myVotes.includes(review.id) ? ' rv__card-helpful--voted' : ''}`}
                          onClick={() => handleHelpful(review.id)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                          </svg>
                          <span>Helpful{review.helpful_count > 0 ? ` (${review.helpful_count})` : ''}</span>
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ═══ BOTTOM CTA ═══ */}
      {!loading && reviews.length > 3 && (
        <section className="rv__bottom-cta rv__anim rv__anim--6">
          <div className="rv__bottom-cta-bg" />
          <div className="rv__bottom-cta-inner">
            <h3>Every experience is unique</h3>
            <p>Share yours and inspire the next guest</p>
            {user ? (
              <button className="rv__bottom-cta-btn" onClick={() => { setBookOpen(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                Add Your Story
              </button>
            ) : (
              <Link to="/login" className="rv__bottom-cta-btn">
                Sign In to Share
              </Link>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
